import type { ModelScopesDto, Site } from '@bk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiObjectUrl } from '../../lib/api';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { SectionLoader } from '../../ui';

/**
 * I due livelli del gestionale (ADR 0210 § 8): tutta l'azienda e ogni sede. Il compositore dice dove stanno i dati di ogni
 * tipo (`/model/scopes`); il menu mostra i tipi del livello scelto e le pagine dei dati partono dalla sede scelta.
 */

const key = (tenantId: string) => `bk:livello:${tenantId}`;

export function readLevel(tenantId: string): string | null {
  try {
    return localStorage.getItem(key(tenantId));
  } catch {
    return null;
  }
}

export function writeLevel(tenantId: string, siteId: string | null): void {
  try {
    if (siteId) localStorage.setItem(key(tenantId), siteId);
    else localStorage.removeItem(key(tenantId));
  } catch {
    /* il livello dura quanto la pagina */
  }
}

export interface Levels {
  /** Ci sono più sedi con dati propri e l'ambito è composto: il gestionale ha i due livelli. */
  multi: boolean;
  sedi: ModelScopesDto['sedi'];
  /** I tipi del livello: dell'azienda o della sede; null quando il gestionale non ha i livelli (si vede tutto). */
  allowed: Set<string> | null;
  /** I tipi che hanno un ambito: quelli senza (da una lettura, da un programma) stanno nel livello dell'azienda. */
  scoped: Set<string>;
  /** La sede scelta, se c'è ancora. */
  site: Site | null;
}

/** I livelli del gestionale e quello scelto; una sede che non c'è più riporta a tutta l'azienda. */
export function useLevels(): Levels {
  const { livello, setLivello } = useTenant();
  const scopes = useTenantQuery<ModelScopesDto>(['model-scopes'], '/model/scopes');
  const sitesQ = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const data = scopes.data;
  const multi = !!data && data.senza_ambito === 0 && data.sedi.length > 1;
  const current = multi ? data.sedi.find((s) => s.id === livello) : undefined;
  useEffect(() => {
    if (data && livello && !current) setLivello(null);
  }, [data, livello, current, setLivello]);
  const scoped = new Set<string>([...(data?.azienda ?? []), ...(data?.sedi ?? []).flatMap((s) => s.aree)].flatMap((a) => a.tipi.map((t) => t.chiave)));
  const tipi = (aree: ModelScopesDto['azienda']) => new Set(aree.flatMap((a) => a.tipi.map((t) => t.chiave)));
  return {
    multi,
    sedi: data?.sedi ?? [],
    allowed: !multi ? null : current ? tipi(current.aree) : tipi(data!.azienda),
    scoped,
    site: (current && sitesQ.data?.data.find((s) => s.id === current.id)) ?? null,
  };
}

/** Un tipo si vede in questo livello? Senza livelli tutto; un tipo senza ambito solo nell'azienda. */
export function visibleIn(levels: Levels, typeKey: string, inSite: boolean): boolean {
  if (!levels.allowed) return true;
  if (!levels.scoped.has(typeKey)) return !inSite;
  return levels.allowed.has(typeKey);
}

/**
 * Una voce dei dati sta in questo livello? Dentro una sede quelle legate alla sede, nell'azienda quelle che non stanno in
 * nessuna sede (ADR 0210 § 4); senza livelli tutte.
 */
export function rowInLevel(levels: Levels, legami: Array<{ sede_id: string | null; stato: string }>): boolean {
  if (!levels.allowed) return true;
  const places = legami.filter((l) => l.stato === 'attivo' && l.sede_id).map((l) => l.sede_id);
  return levels.site ? places.includes(levels.site.id) : !places.length;
}

/** Il logo di una sede, se ne ha uno suo. */
export function useSiteLogo(site: Site | null): string | null {
  const { tenant } = useTenant();
  const q = useQuery({
    queryKey: ['tenant', tenant.id, 'site-logo', site?.id ?? null],
    queryFn: () => apiObjectUrl(`/tenants/${tenant.id}/brand/sites/${site!.id}/logo`),
    enabled: !!site?.marchio?.logo,
    staleTime: Infinity,
  });
  return site?.marchio?.logo ? (q.data ?? null) : null;
}

/** Una scheda di sede per entrarci: nome, luogo, attività e logo (ADR 0210 § 8). */
function SiteTile({ s, current = false, onEnter }: { s: Site; current?: boolean; onEnter: () => void }) {
  const logo = useSiteLogo(s);
  const place = [s.address, s.municipality, s.province && `(${s.province})`].filter(Boolean).join(' ');
  const acts = s.attivita_in_sede.filter((a) => a.stato !== 'esclusa');
  return (
    <button
      type="button"
      onClick={onEnter}
      className={`flex min-w-0 flex-col gap-2 rounded-xl border bg-white p-4 text-left shadow-sm transition hover:border-accent/60 hover:shadow ${current ? 'border-accent ring-2 ring-accent/20' : 'border-black/[0.08]'}`}
    >
      <div className="flex items-center gap-3">
        {logo ? <img src={logo} alt="" className="h-8 max-w-[6rem] object-contain" /> : <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent/10 text-sm font-semibold text-accent">{s.name.slice(0, 1)}</span>}
        <span className="min-w-0 truncate font-semibold text-ink">{s.name}</span>
      </div>
      <span className="text-xs text-ink/55">{place || 'Indirizzo da completare'}</span>
      {acts.length > 0 && <span className="text-xs text-ink/70">{acts.map((a) => a.name).join(' · ')}</span>}
      <span className="mt-1 text-xs font-medium text-accent">{current ? 'Ci sei ora · entra' : 'Entra nella sede'} →</span>
    </button>
  );
}

/** Le sedi del gestionale nell'ordine dei livelli, con la loro scheda. */
function useLevelSites(): Site[] {
  const levels = useLevels();
  const sitesQ = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  return levels.sedi.map((x) => sitesQ.data?.data.find((s) => s.id === x.id)).filter((s): s is Site => !!s);
}

/** Nella dashboard dell'azienda con più sedi: le sedi in cui entrare (ADR 0210 § 8, ADR 0212). */
export function SiteChooser() {
  const { tenant, setLivello } = useTenant();
  const levels = useLevels();
  const sites = useLevelSites();
  if (!levels.multi || levels.site || !sites.length) return null;
  return (
    <section className="mb-6 space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-ink">Le sedi</h2>
        <p className="text-sm text-ink/60">Qui sei nell’azienda: società, conti, fisco, personale e ciò che vale per tutte le sedi. Entra in una sede per i suoi dati.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sites.map((s) => (
          <SiteTile
            key={s.id}
            s={s}
            onEnter={() => {
              setLivello(s.id);
              navigate(`/t/${tenant.id}/dashboard`);
            }}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * La prima cosa entrando in un'azienda con più sedi (ADR 0212): dove si lavora, a tutta pagina. Il livello scelto
 * l'ultima volta è segnato; la scelta porta alla dashboard di quel livello.
 */
export function LevelChooserPage() {
  const { tenant, livello, setLivello } = useTenant();
  const levels = useLevels();
  const sites = useLevelSites();
  const enter = (siteId: string | null) => {
    setLivello(siteId);
    navigate(`/t/${tenant.id}/dashboard`);
  };
  const scopes = useTenantQuery<ModelScopesDto>(['model-scopes'], '/model/scopes');
  // Con una sola sede non c'è niente da scegliere.
  const single = (!!scopes.data || scopes.isError) && !levels.multi;
  useEffect(() => {
    if (single) window.location.replace(`#/t/${tenant.id}/dashboard`);
  }, [single, tenant.id]);
  if (!levels.multi) return <SectionLoader />;
  return (
    <div className="mx-auto max-w-4xl py-4">
      <h1 className="text-2xl font-semibold text-ink">Dove lavori oggi?</h1>
      <p className="mt-1 text-sm text-ink/60">
        {tenant.name} ha {levels.sedi.length} sedi. Scegli una sede per i suoi dati, le sue persone e i suoi documenti, oppure tutta l’azienda per ciò che vale per tutte. Puoi cambiare quando vuoi dalla barra in alto.
      </p>
      <button
        type="button"
        onClick={() => enter(null)}
        className={`mt-6 flex w-full items-center gap-4 rounded-xl border bg-white p-5 text-left shadow-sm transition hover:border-accent/60 hover:shadow ${!livello ? 'border-accent ring-2 ring-accent/20' : 'border-black/[0.08]'}`}
      >
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-lg font-semibold text-white">{tenant.name.slice(0, 1)}</span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-ink">Tutta l’azienda</span>
          <span className="block text-sm text-ink/60">Società, organi e cariche, conti, fisco, personale e ciò che vale per tutte le sedi.</span>
        </span>
        <span className="shrink-0 text-sm font-medium text-accent">{!livello ? 'Ci sei ora · entra' : 'Entra'} →</span>
      </button>
      <h2 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wide text-ink/50">Le sedi</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sites.map((s) => (
          <SiteTile key={s.id} s={s} current={livello === s.id} onEnter={() => enter(s.id)} />
        ))}
      </div>
    </div>
  );
}

/** Nella barra in alto, su ogni pagina e anche sul telefono: dove si lavora, e il passaggio alla scelta (ADR 0212). */
export function LevelSwitch() {
  const { tenant } = useTenant();
  const levels = useLevels();
  const logo = useSiteLogo(levels.site);
  if (!levels.multi) return null;
  return (
    <a
      href={`#/t/${tenant.id}/sede`}
      title="Cambia sede"
      className="flex min-w-0 max-w-[7.5rem] shrink items-center gap-2 rounded-[10px] sm:max-w-[16rem] border border-accent/30 bg-accent/[0.07] px-2.5 py-1.5 text-left transition hover:border-accent/60 hover:bg-accent/10"
    >
      {logo ? (
        <img src={logo} alt="" className="h-6 max-w-[3rem] shrink-0 object-contain" />
      ) : (
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-accent" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
          <circle cx="12" cy="9.5" r="2.5" />
        </svg>
      )}
      <span className="min-w-0">
        <span className="block text-[10px] font-medium uppercase leading-tight tracking-wide text-ink/50">{levels.site ? 'Sede' : 'Sei in'}</span>
        <span className="block truncate text-[13px] font-semibold leading-tight text-ink">{levels.site ? levels.site.name : 'Tutta l’azienda'}</span>
      </span>
      <svg viewBox="0 0 24 24" className="hidden h-4 w-4 shrink-0 text-ink/40 sm:block" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="m7 10 5 5 5-5" />
      </svg>
    </a>
  );
}
